/**
 * Hooks for the agent-platform Intelligence surfaces (ADR-0009…0012):
 * Knowledge / RAG, Memory, Evals & Model Governance, and the Agent Factory.
 *
 * They follow the existing Intelligence-hook pattern (useState + useCallback +
 * useEffect, manual refresh, `available` flips false when an endpoint is
 * unreachable so the page renders a graceful empty state rather than crashing).
 */
import { useCallback, useEffect, useState } from 'react';

import { apiClient } from '../api/client';

// --- shared shapes ---------------------------------------------------------

export interface KnowledgeCollection {
  id: string;
  name: string;
  description?: string;
  grain_count?: number;
}

export interface RetrievedChunk {
  grain_id: string;
  collection_id: string;
  title: string;
  text: string;
  score: number;
  citation: string;
}

export interface MemoryItem {
  id: string;
  scope: string;
  subject_id: string;
  key: string;
  value: string;
  confidence: number;
  source: string;
  approved: boolean;
}

export interface GoldenDataset {
  id: string;
  name: string;
  capability: string;
  examples?: unknown[];
}

export interface GateResult {
  capability: string;
  candidate_version: string;
  champion_version: string;
  passed: boolean;
  approval_gate: string;
  bias_review_status: string;
  review_task_id?: string | null;
}

export interface AgentDefinition {
  id: string;
  name: string;
  description?: string;
  version: number;
  status: string;
  graph: { nodes: { id: string; type: string; label?: string }[]; edges: unknown[] };
}

export interface ToolSpec {
  name: string;
  description?: string;
  side_effect: string;
  target_service?: string | null;
  requires_approval?: boolean | null;
}

interface ListResult<T> {
  items: T[];
  available: boolean;
  isLoading: boolean;
  refresh: () => void;
}

function useList<T>(path: string): ListResult<T> {
  const [items, setItems] = useState<T[]>([]);
  const [available, setAvailable] = useState(true);
  const [isLoading, setIsLoading] = useState(false);

  const refresh = useCallback(() => {
    setIsLoading(true);
    apiClient
      .get<{ items?: T[] }>(path)
      .then((data) => {
        setItems(data.items ?? []);
        setAvailable(true);
      })
      .catch(() => {
        setItems([]);
        setAvailable(false);
      })
      .finally(() => setIsLoading(false));
  }, [path]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return { items, available, isLoading, refresh };
}

// --- typed wrappers --------------------------------------------------------

export function useKnowledgeCollections(): ListResult<KnowledgeCollection> {
  return useList<KnowledgeCollection>('/knowledge/collections');
}

export function useMemoryItems(): ListResult<MemoryItem> {
  return useList<MemoryItem>('/memory/items');
}

export function useGoldenDatasets(): ListResult<GoldenDataset> {
  return useList<GoldenDataset>('/evals/datasets');
}

export function useAgentDefinitions(): ListResult<AgentDefinition> {
  return useList<AgentDefinition>('/agents');
}

export function useAgentTools(): ListResult<ToolSpec> {
  return useList<ToolSpec>('/ai/tools');
}

// --- imperative helpers (used by page actions) -----------------------------

export function searchKnowledge(
  text: string,
): Promise<{ chunks: RetrievedChunk[]; count: number }> {
  return apiClient.post<{ chunks: RetrievedChunk[]; count: number }>('/knowledge/search', {
    text,
    top_k: 5,
  });
}

export function runRegressionGate(
  capability: string,
  candidateVersion: string,
): Promise<GateResult> {
  const params = new URLSearchParams({ capability, candidate_version: candidateVersion });
  return apiClient.post<GateResult>(`/evals/gate?${params.toString()}`);
}
